import React, { useEffect, useState } from 'react';
import {
  Database, RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck, Download, HardDrive, Play, History, Info, Copy, Terminal,
} from 'lucide-react';
import { ApiClient } from '../../api/client';

// ═══════════════════════════════════════════════════════════════
// Adatbázis-séma ellenőrzés, teljes mentés és frissítés (superadmin)
// Háttér: worker/routes/schema-admin.js, elvárt séma: worker/core/schema-expected.js
// A frissítés csak HOZZÁAD (tábla, oszlop, index), előtte mindig teljes mentés készül.
// ═══════════════════════════════════════════════════════════════

type Status = Awaited<ReturnType<typeof ApiClient.getSchemaStatus>>;
type ApplyResult = Awaited<ReturnType<typeof ApiClient.applySchemaUpdate>>;

const fmtBytes = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const fmtDate = (s: string) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleString('hu-HU'); };

const BACKUP_CMD = 'npm run db:backup:prod';
const RESTORE_CMD = 'npm run db:restore:prod -- --file backups/<mentés>.sql';
const describeBackup = (b: { tables: number | null; rows: number | null; bytes: number; format?: string }) =>
  b.format === 'cloudflare-export' ? `Cloudflare-export, ${fmtBytes(b.bytes)}` : `${b.tables} tábla, ${b.rows} sor, ${fmtBytes(b.bytes)}`;

export const SchemaMaintenanceView: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [backups, setBackups] = useState<{ key: string; size: number; uploaded: string }[]>([]);
  const [history, setHistory] = useState<{ run_id: string; created_at: string; backup_key: string | null; steps: number; ok_steps: number; error: string | null }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, b, h] = await Promise.all([ApiClient.getSchemaStatus(), ApiClient.listFullBackups(), ApiClient.getSchemaHistory()]);
      if (s.ok) setStatus(s); else setError(s.error || 'Az ellenőrzés nem sikerült');
      if (b.ok && b.data) setBackups(b.data);
      if (h.ok && h.data) setHistory(h.data);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (key?: string) => {
    setBusy(key || 'now'); setError(null);
    try {
      const { blob, filename } = await ApiClient.downloadFullBackup(key);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (e: any) { setError(`A letöltés nem sikerült: ${e.message}`); }
    setBusy(null);
  };

  const backupNow = async () => {
    setBusy('store'); setError(null);
    const r = await ApiClient.createFullBackup();
    setBusy(null);
    if (!r.ok || !r.backup) { setError(r.error || 'A mentés nem sikerült'); return; }
    setNotice(`Teljes mentés elkészült (${describeBackup(r.backup)}).${r.backup.warning ? ` ${r.backup.warning}` : ''}`);
    setTimeout(() => setNotice(null), 9000);
    load();
  };

  const apply = async () => {
    if (!status) return;
    setApplying(true); setError(null); setResult(null);
    const r = await ApiClient.applySchemaUpdate(status.plan_hash);
    setApplying(false); setConfirmText('');
    if (!r.ok && !r.results) { setError(r.plan_changed ? 'Az adatbázis az ellenőrzés óta megváltozott – ellenőrizd újra.' : (r.error || 'A frissítés nem sikerült')); load(); return; }
    setResult(r);
    load();
  };

  const copy = (t: string) => { try { navigator.clipboard.writeText(t); setNotice('Vágólapra másolva.'); setTimeout(() => setNotice(null), 2000); } catch { /* ignore */ } };

  const card = 'rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900';
  const steps = status?.steps || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5 min-w-0 w-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"><Database className="w-5 h-5" /></div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Adatbázis-séma és teljes mentés</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-2xl">
              Összeveti az adatbázist a program által elvárt szerkezettel, és egy lépésben pótolja a hiányzó táblákat, oszlopokat és indexeket. Csak hozzáad: meglévő adatot nem töröl és nem módosít. Előtte mindig teljes mentés készül.
            </p>
          </div>
        </div>
        <button onClick={load} className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 cursor-pointer" title="Újraellenőrzés">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {notice && <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{notice}</div>}
      {error && <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{error}</div>}

      {/* ── Állapot ── */}
      {status && (
        <section className={`${card} p-4 sm:p-5 space-y-3`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {status.up_to_date
                ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" /><span className="font-semibold text-sm">Az adatbázis szerkezete naprakész</span></>
                : <><AlertTriangle className="w-5 h-5 text-amber-500" /><span className="font-semibold text-sm">{steps.length} hiányzó elem</span></>}
            </div>
            <span className="text-xs text-slate-500">Elvárt séma: v{status.expected_version}</span>
          </div>

          {!status.up_to_date && (
            <>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                {steps.map((s, i) => (
                  <li key={i} className="p-2.5 flex flex-col gap-0.5">
                    <span className="font-medium text-slate-800 dark:text-slate-100">{s.label}</span>
                    {s.notes && s.notes.length > 0 && <span className="text-amber-700 dark:text-amber-400">{s.notes.join(', ')}</span>}
                    <code className="text-[10.5px] text-slate-500 break-all">{s.sql.length > 220 ? `${s.sql.slice(0, 220)}…` : s.sql}</code>
                  </li>
                ))}
              </ul>

              <div className="p-3.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-900 dark:text-indigo-200 space-y-1.5">
                <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="w-4 h-4" /> Mi történik a jóváhagyás után</div>
                <ol className="list-decimal pl-5 space-y-0.5">
                  <li>Teljes mentés készül a tárhelyre: minden tábla szerkezete és teljes adata. Ha ez nem sikerül, a frissítés el sem indul.</li>
                  <li>A fenti lépések egyenként futnak. Hiba esetén a frissítés ott megáll, a már elkészült lépések megmaradnak.</li>
                  <li>A mentés a lap alján letölthető; ebből a frissítés előtti állapot parancssorból visszaállítható.</li>
                </ol>
                {!status.backup_storage && <p className="text-rose-600 font-semibold">Ebben a környezetben nincs beállított tárhely (R2), ezért a frissítés nem indítható. Először tölts le egy teljes mentést.</p>}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs flex items-center gap-2">Jóváhagyáshoz írd be: <b>FRISSÍTÉS</b>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 w-32" />
                </label>
                <button onClick={apply} disabled={applying || !status.backup_storage || confirmText.trim().toUpperCase() !== 'FRISSÍTÉS'}
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-2 cursor-pointer disabled:opacity-50">
                  {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Mentés és frissítés
                </button>
              </div>
            </>
          )}

          {(status.extra_tables.length > 0 || status.extra_columns.length > 0) && (
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Az elvárt sémán felüli elemek ({status.extra_tables.length + status.extra_columns.length}) – ezekhez a frissítés nem nyúl</summary>
              <div className="mt-1.5 font-mono break-all">{[...status.extra_tables.map((t) => `tábla: ${t}`), ...status.extra_columns.map((c) => `oszlop: ${c}`)].join(' · ')}</div>
            </details>
          )}
        </section>
      )}

      {/* ── Eredmény ── */}
      {result && (
        <section className={`${card} p-4 sm:p-5 space-y-2 ${result.ok ? 'border-emerald-300' : 'border-rose-300'}`}>
          <div className="flex items-center gap-2 font-semibold text-sm">
            {result.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-rose-600" />}
            {result.nothing_to_do ? 'Nem volt teendő.' : result.ok ? 'A frissítés sikeresen lefutott.' : `A frissítés megállt: ${result.failed?.label}`}
          </div>
          {result.failed && <p className="text-xs text-rose-600 break-all">{result.failed.error}</p>}
          {result.backup?.warning && <p className="text-xs text-amber-700 dark:text-amber-400">{result.backup.warning}</p>}
          {result.backup && (
            <div className="text-xs text-slate-600 dark:text-slate-300 flex flex-wrap items-center gap-2">
              Előtte készült mentés: <code className="break-all">{result.backup.key}</code> ({describeBackup(result.backup)})
              <button onClick={() => save(result.backup!.key)} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1 cursor-pointer"><Download className="w-3.5 h-3.5" /> Letöltés</button>
            </div>
          )}
          {result.results && result.results.length > 0 && (
            <ul className="text-xs space-y-0.5">
              {result.results.map((r, i) => <li key={i} className={r.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600'}>{r.ok ? '✓' : '✗'} {r.label}</li>)}
            </ul>
          )}
        </section>
      )}

      {/* ── Teljes mentések ── */}
      <section className={`${card} p-4 sm:p-5 space-y-3`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-bold flex items-center gap-2"><HardDrive className="w-4 h-4 text-indigo-500" /> Teljes mentések</h2>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save()} disabled={!!busy} className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
              {busy === 'now' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Mentés letöltése most
            </button>
            <button onClick={backupNow} disabled={!!busy || !status?.backup_storage} className="px-3 py-1.5 rounded-xl bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
              {busy === 'store' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <HardDrive className="w-3.5 h-3.5" />} Mentés a tárhelyre
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500">Minden tábla szerkezete és teljes adata, egy SQL-fájlban (a mellékletfájlok nem; azok a tárhelyen maradnak).</p>
        {status && (
          <p className={`text-xs ${status.backup_mode === 'cloudflare-export' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}`}>
            {status.backup_mode === 'cloudflare-export'
              ? 'Mentés módja: Cloudflare D1 export – a Cloudflare oldalán készül, számítási időkorlát nélkül.'
              : 'Mentés módja: a Worker készíti. Élesben nagy adatbázisnál beállítható a Cloudflare D1 export (lásd élesítési útmutató, G fejezet).'}
          </p>
        )}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 text-xs">
          {backups.length === 0 && <div className="p-3 text-slate-400">Még nincs tárolt teljes mentés.</div>}
          {backups.map((b) => (
            <div key={b.key} className="p-2.5 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0"><div className="font-mono break-all">{b.key.replace('backups/full/', '')}</div><div className="text-slate-500">{fmtDate(b.uploaded)} · {fmtBytes(b.size)}</div></div>
              <button onClick={() => save(b.key)} disabled={!!busy} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1 cursor-pointer disabled:opacity-50">
                {busy === b.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Letöltés
              </button>
            </div>
          ))}
        </div>
        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs space-y-1.5">
          <div className="font-semibold flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> Parancssorból (a projektmappában)</div>
          {[['Teljes mentés élesről (Cloudflare-export):', BACKUP_CMD], ['Visszaállítás mentésből (a letöltött fájlt tedd a backups/ mappába):', RESTORE_CMD]].map(([label, cmd]) => (
            <div key={cmd} className="space-y-0.5">
              <div className="text-slate-500">{label}</div>
              <div className="flex items-start gap-2">
                <code className="flex-1 break-all text-[11px]">{cmd}</code>
                <button onClick={() => copy(cmd)} className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer" title="Másolás"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <p className="text-slate-500">A visszaállítás előbb az aktuális állapotot is elmenti, megerősítést kér, majd pontosan a mentéskori szerkezetet és adatot állítja vissza. VPS-en: <code>node scripts/d1-restore.mjs --env vps --file …</code>, helyben: <code>npm run db:restore:local -- --file …</code>.</p>
        </div>
      </section>

      {/* ── Napló ── */}
      {history.length > 0 && (
        <section className={`${card} p-4 sm:p-5 space-y-2`}>
          <h2 className="text-sm font-bold flex items-center gap-2"><History className="w-4 h-4 text-indigo-500" /> Korábbi sémafrissítések</h2>
          <ul className="text-xs divide-y divide-slate-100 dark:divide-slate-800">
            {history.map((h) => (
              <li key={h.run_id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <span>{fmtDate(h.created_at)} · {h.ok_steps}/{h.steps} lépés {h.error ? <span className="text-rose-600">– hiba: {h.error}</span> : <span className="text-emerald-600">rendben</span>}</span>
                {h.backup_key && <button onClick={() => save(h.backup_key!)} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1 cursor-pointer"><Download className="w-3.5 h-3.5" /> Előtte készült mentés</button>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default SchemaMaintenanceView;
